import { useCallback, useMemo, useState } from "react";
import { computeSharedYBounds, niceDomain, quarterTicks, sliderStep, type YBounds } from "./sharedYDomain";

export type SharedYAxisRange = {
    /** 縦軸統一の ON / OFF。OFF では従来どおりセルごとの auto scale に戻る。 */
    unified: boolean;
    setUnified: (unified: boolean) => void;
    /** 描画中データ全体の全域（nice 丸め後）。有効値が無ければ undefined。 */
    bounds: YBounds | undefined;
    /** 現在の選択範囲。未操作時は bounds と同じ。 */
    range: YBounds | undefined;
    setRange: (range: YBounds) => void;
    /** 選択範囲を全域へ戻す。 */
    reset: () => void;
    /** スライダーの刻み。 */
    step: number;
    /** YAxis に渡す domain。統一 OFF / 有効値なしでは undefined（＝recharts 既定）。 */
    domain: YBounds | undefined;
    /** YAxis に渡す目盛り（domain を4等分）。domain が無ければ undefined。 */
    ticks: number[] | undefined;
};

/**
 * 条件全体表示グリッドの縦軸レンジを 1 ページ 1 状態で保持する。
 * 全セルが同じ domain を参照するため、スライダー操作は自動的に全グラフへ同期する。
 */
export function useSharedYAxisRange(rawBounds: YBounds | undefined): SharedYAxisRange {
    const [unified, setUnified] = useState(true);

    const min = rawBounds?.[0];
    const max = rawBounds?.[1];
    const bounds = useMemo(
        () => (min !== undefined && max !== undefined ? niceDomain([min, max]) : undefined),
        [min, max]
    );

    const boundsKey = bounds ? `${bounds[0]}:${bounds[1]}` : "";
    // 計算セット / gear / ユニットID・現象グループID の変更でデータ全域が変わったら、
    // 絞り込みの意味も変わるため選択範囲は全域へ戻す（key 不一致なら bounds を採用）。
    const [selection, setSelection] = useState<{ key: string; range: YBounds }>();
    const range = selection?.key === boundsKey ? selection.range : bounds;

    const setRange = useCallback(
        (next: YBounds) => setSelection({ key: boundsKey, range: next }),
        [boundsKey]
    );
    const reset = useCallback(() => setSelection(undefined), []);
    const step = useMemo(() => (bounds ? sliderStep(bounds) : 1), [bounds]);
    const domain = unified ? range : undefined;

    return {
        unified,
        setUnified,
        bounds,
        range,
        setRange,
        reset,
        step,
        domain,
        ticks: domain ? quarterTicks(domain) : undefined,
    };
}

export type CellYAxis = {
    /** そのセルの YAxis に渡す domain。有効値が無ければ undefined（＝recharts 既定）。 */
    domain: YBounds | undefined;
    /** そのセルの YAxis に渡す目盛り（domain を4等分）。domain が無ければ undefined。 */
    ticks: number[] | undefined;
};

/**
 * セル（1グラフ）単位で YAxis に渡す domain / ticks を求める。
 * 統一 ON では共有の domain / ticks をそのまま使う。統一 OFF ではそのセル自身の描画系列から
 * nice domain を求め、4等分の目盛りを付ける（セルごとの auto scale という意味は保ったまま、
 * 目盛りの間隔だけは常に等しくする）。
 */
export function resolveCellYAxisRange(
    cellSeries: ReadonlyArray<ReadonlyArray<number | null | undefined> | undefined>,
    shared: SharedYAxisRange
): CellYAxis {
    if (shared.unified) {
        return { domain: shared.domain, ticks: shared.ticks };
    }
    const bounds = computeSharedYBounds(cellSeries);
    if (!bounds) return { domain: undefined, ticks: undefined };
    const domain = niceDomain(bounds);
    return { domain, ticks: quarterTicks(domain) };
}
